import { formatMoneyDisplay } from 'src/shared/lib/money';
import { showDateFromRange } from './detail-file-date';
import { getProgrammingProvider } from '../model/programming-service';

export function calculatePaxCount(lstPaX: any[] | undefined | null): number {
  return (
    lstPaX?.filter((pax) => pax.type === 'pax' && pax.estado).length ?? 0
  );
}

export function calculateTcCount(lstPaX: any[] | undefined | null): number {
  return lstPaX?.filter((pax) => pax.type === 'tc' && pax.estado).length ?? 0;
}

export function ceilServiceAmount(value: number | null | undefined): string {
  return formatMoneyDisplay(value);
}

export function showServiceDate(dateRange: string, day: string): Date {
  return showDateFromRange(dateRange, day);
}

function normalizeEstadoTicketValue(estado: string | null | undefined): string {
  if (!estado) return '';
  const key = String(estado).toUpperCase().replace(/\s+/g, '_');
  if (key === 'CREATED') return 'CREADO';
  if (key === 'ENPROCESO') return 'EN_PROCESO';
  return key;
}

/**
 * Padre TICKET: la OS vive en tickets hijos.
 * Si hay al menos un ticket CREADO sin OS, el servicio sigue seleccionable
 * aunque el padre tenga order residual o hasOrderedTickets=true.
 */
export function hasAvailableTicketsForOs(row: any): boolean {
  if (Array.isArray(row?.tickets) && row.ticketsLoaded) {
    return row.tickets.some((ticket: any) => {
      const estado = normalizeEstadoTicketValue(ticket?.estadoTicket);
      const hasOrder = Boolean(ticket?.order?.orderId);
      return !hasOrder && estado === 'CREADO';
    });
  }

  // Sin expand: no bloquear por order del padre (puede haber tickets libres).
  const estado = String(row?.estadoServicioTickets ?? '')
    .toUpperCase()
    .replace(/\s+/g, '_');
  if (estado === 'RESERVADO') {
    return false;
  }
  return true;
}

export function isTicketServiceRow(row: any): boolean {
  return String(row?.tipoServicio ?? '').toUpperCase() === 'TICKET';
}

/** Tickets libres marcados en el expand (para createOrder.ticketIds). */
export function getCheckedFreeTicketIds(row: any): string[] {
  if (!Array.isArray(row?.tickets)) return [];
  return row.tickets
    .filter(
      (ticket: any) =>
        Boolean(ticket?.checked) &&
        !ticket?.order?.orderId &&
        String(ticket?.id || '').trim()
    )
    .map((ticket: any) => String(ticket.id));
}

/**
 * Arma items de createOrder desde filas seleccionadas.
 * TICKET con tickets checked → ticketIds; si no, fan-out (solo serviceId).
 */
export function buildCreateOrderItemsFromSelectedRows(
  rows: any[],
  isSelectable: (row: any) => boolean
): Array<{ serviceId: string; ticketIds?: string[] }> {
  const items: Array<{ serviceId: string; ticketIds?: string[] }> = [];

  for (const row of rows ?? []) {
    if (!isSelectable(row)) continue;

    const serviceId = String(row?.id || '').trim();
    if (!serviceId) continue;

    const isTicket = isTicketServiceRow(row);
    const checkedTicketIds = isTicket ? getCheckedFreeTicketIds(row) : [];
    const parentChecked = Boolean(row?.checked);
    const hasTicketSelection = checkedTicketIds.length > 0;

    if (!parentChecked && !hasTicketSelection) continue;

    if (hasTicketSelection) {
      items.push({ serviceId, ticketIds: checkedTicketIds });
      continue;
    }

    // Padre marcado sin tickets checked → fan-out (o servicio no-TICKET).
    items.push({ serviceId });
  }

  return items;
}

/**
 * Catálogo SSFF en file hijo/regular: no Generar OS ni editar ticket/monto.
 * - File padre (isSSFF / isSSFFPadre): como file normal → no bloquea.
 * - serviceAdd / upgrade (ssffproduct sin productId): no bloquea.
 */
export function isSsffCatalogBlockedOnChild(
  row: {
    ssffproduct?: unknown;
    serviceAdd?: unknown;
    productId?: unknown;
    isSSFFPadre?: unknown;
  } | null
  | undefined,
  isParentFile = false
): boolean {
  if (isParentFile || Boolean(row?.isSSFFPadre)) {
    return false;
  }
  if (Boolean(row?.serviceAdd)) {
    return false;
  }
  // Upgrade: ssffproduct sin productId
  if (Boolean(row?.ssffproduct) && !row?.productId) {
    return false;
  }
  return Boolean(row?.ssffproduct) && Boolean(row?.productId);
}

export function isAlimentosYBebidasServiceRow(row: any): boolean {
  return String(row?.tipoServicio ?? '') === 'ALIMENTOSYBEBIDAS';
}

export function hasPurchaseNote(row: any): boolean {
  return Boolean(row?.purchaseNote?.purchaseNoteId || row?.purchaseNoteId);
}

/**
 * Alimentos y bebidas: seleccionable si falta OS y/o NP
 * (puede generar el documento que aún no tenga).
 */
export function isAlimentosDocumentRowSelectable(
  row: any,
  isSSFF: boolean
): boolean {
  if (!isAlimentosYBebidasServiceRow(row)) {
    return false;
  }
  if (isSsffCatalogBlockedOnChild(row, isSSFF)) {
    return false;
  }
  return !hasServiceOrder(row) || !hasPurchaseNote(row);
}

/**
 * @deprecated Prefer isAlimentosDocumentRowSelectable (OS + NP).
 * Alimentos: seleccionable para NP si aún no tiene purchaseNoteId.
 */
export function isPurchaseNoteRowSelectable(
  row: any,
  isSSFF: boolean
): boolean {
  if (!isAlimentosYBebidasServiceRow(row)) {
    return false;
  }
  if (isSsffCatalogBlockedOnChild(row, isSSFF)) {
    return false;
  }
  return !hasPurchaseNote(row);
}

export function isServiceRowSelectable(row: any, isSSFF: boolean): boolean {
  if (isAlimentosYBebidasServiceRow(row)) {
    return isAlimentosDocumentRowSelectable(row, isSSFF);
  }

  if (isTicketServiceRow(row)) {
    if (isSsffCatalogBlockedOnChild(row, isSSFF)) {
      return false;
    }
    return hasAvailableTicketsForOs(row);
  }

  if (isSSFF) {
    return !hasServiceOrder(row);
  }

  if (isSsffCatalogBlockedOnChild(row, false)) {
    return false;
  }
  return !hasServiceOrder(row);
}

/** Items createPurchaseNote: solo serviceId sin NP (nunca ticketIds). */
export function buildCreatePurchaseNoteItemsFromSelectedRows(
  rows: any[],
  isSelectable: (row: any) => boolean
): Array<{ serviceId: string }> {
  const items: Array<{ serviceId: string }> = [];

  for (const row of rows ?? []) {
    if (
      !row?.checked ||
      !isSelectable(row) ||
      !isAlimentosYBebidasServiceRow(row) ||
      hasPurchaseNote(row)
    ) {
      continue;
    }
    const serviceId = String(row?.id ?? '').trim();
    if (!serviceId) {
      continue;
    }
    items.push({ serviceId });
  }

  return items;
}

export function getServiceOrder(row: any): any {
  return row?.order ?? null;
}

export function hasServiceOrder(row: any): boolean {
  return Boolean(row?.order?.orderId);
}

/** Core order o legacy numOS. */
export function hasBlockingServiceOrder(row: any): boolean {
  return Boolean(
    row?.order?.orderId || row?.orderId || row?.numOS
  );
}

export function hasOrderedTickets(row: any): boolean {
  return Boolean(row?.hasOrderedTickets);
}

/** OS oficial congela el costo. Preliminar (orderIssuanceFrozen=false) no. */
function isCostFrozenByPreliminaryOrder(row: any): boolean {
  if (row?.orderIssuanceFrozen === false) {
    return false;
  }
  if (row?.orderIssuanceFrozen === true) {
    return true;
  }
  if (hasBlockingServiceOrder(row)) {
    return true;
  }
  const tipo = String(row?.tipoServicio ?? '')
    .trim()
    .toUpperCase();
  return tipo === 'TICKET' && hasOrderedTickets(row);
}

/** La fila ya tiene OS y sigue PRELIMINAR: el monto se edita y la OS se recalcula. */
function hasPreliminaryServiceOrder(row: any): boolean {
  if (!hasBlockingServiceOrder(row)) {
    return false;
  }
  const status = String(row?.order?.orderStatus ?? '')
    .trim()
    .toUpperCase();
  return status === 'PRELIMINAR' || row?.orderIssuanceFrozen === false;
}

/**
 * Editar monto de servicio antes de OS/NP.
 * Misma base que seleccionar fila + bloqueo por orderId / hasOrderedTickets /
 * purchaseNoteId / hasPurchaseNotedTickets.
 */
export function canEditServiceAmount(row: any, isSSFF = false): boolean {
  if (hasPurchaseNote(row)) {
    return false;
  }
  const tipo = String(row?.tipoServicio ?? '')
    .trim()
    .toUpperCase();
  if (tipo === 'TICKET' && Boolean(row?.hasPurchaseNotedTickets)) {
    return false;
  }
  // Desde CONFIRMADO en adelante el costo queda congelado.
  if (isCostFrozenByPreliminaryOrder(row)) {
    return false;
  }
  // Con OS PRELIMINAR el monto sí se edita (la OS se recalcula al guardar).
  // El padre TICKET no: su monto se edita por ticket en el expand.
  if (hasPreliminaryServiceOrder(row)) {
    if (isSsffCatalogBlockedOnChild(row, isSSFF)) {
      return false;
    }
    return !(tipo === 'TICKET' && hasOrderedTickets(row));
  }
  if (!isServiceRowSelectable(row, isSSFF)) {
    return false;
  }
  return true;
}

/** Editar monto de ticket (Ticketería / expand). */
export function canEditTicketAmount(
  ticket: any,
  parent?: {
    ssffproduct?: unknown;
    serviceAdd?: unknown;
    productId?: unknown;
    isSSFFPadre?: unknown;
  } | null,
  isParentFile = false
): boolean {
  if (isSsffCatalogBlockedOnChild(parent, isParentFile)) {
    return false;
  }
  if (
    Boolean(ticket?.purchaseNote?.purchaseNoteId || ticket?.purchaseNoteId)
  ) {
    return false;
  }
  // Con OS PRELIMINAR el monto del ticket sí se edita (la OS se recalcula).
  const orderStatus = String(ticket?.order?.orderStatus ?? '')
    .trim()
    .toUpperCase();
  if (orderStatus === 'PRELIMINAR' || ticket?.orderIssuanceFrozen === false) {
    return true;
  }
  return !Boolean(
    ticket?.order?.orderId ||
      ticket?.orderId ||
      ticket?.idOS ||
      ticket?.numOS
  );
}

export function normalizeOrderNumber(
  value: string | number | null | undefined
): string | null {
  if (value == null || value === '') {
    return null;
  }
  return String(value);
}

export function sameOrderNumberValue(
  a: string | number | null | undefined,
  b: string | number | null | undefined
): boolean {
  const normalizedA = normalizeOrderNumber(a);
  const normalizedB = normalizeOrderNumber(b);
  return normalizedA != null && normalizedB != null && normalizedA === normalizedB;
}

export function sameServiceId(
  service: any,
  serviceId: string | number,
  idKey: string = 'id'
): boolean {
  const normalizedTarget = String(serviceId);
  const candidates = [service?.[idKey], service?.id, service?.serviceId].filter(
    (value) => value != null && value !== ''
  );

  return candidates.some((value) => String(value) === normalizedTarget);
}

/**
 * PK de itinerary_services para APIs de tickets (getTicketeriaTicketsByService / createOrder).
 * En programación `id` = PK y `serviceId` = catálogo Sourcing — no invertir.
 * En Ticketería ambos suelen ser el PK.
 */
export function resolveItineraryServicePk(row: any): string | null {
  if (row?.id != null && row.id !== '') {
    return String(row.id);
  }
  if (row?.serviceId != null && row.serviceId !== '') {
    return String(row.serviceId);
  }
  return null;
}

/** Resumen padre TICKET: CREADO si aún no hay OS en tickets; EN_PROCESO si ya hay. */
export function resolveTicketServiceEstado(row: any): string {
  const explicit = String(row?.estadoServicioTickets ?? '')
    .toUpperCase()
    .replace(/\s+/g, '_');
  if (
    explicit === 'CREADO' ||
    explicit === 'EN_PROCESO' ||
    explicit === 'RESERVADO' ||
    explicit === 'NUEVA_ADICION'
  ) {
    return explicit;
  }
  return row?.hasOrderedTickets ? 'EN_PROCESO' : 'CREADO';
}

export function filterServicesByOrderNumber(
  services: any[],
  orderNumber: string | number | null | undefined
): any[] {
  if (orderNumber == null || orderNumber === '') {
    return [];
  }

  return services.filter((item) => {
    if (sameOrderNumberValue(item?.order?.orderNumber, orderNumber)) {
      return true;
    }
    // OS multi-línea TICKET: la OS vive en hijos, no siempre en el padre.
    if (!Array.isArray(item?.tickets)) {
      return false;
    }
    return item.tickets.some((ticket: any) =>
      sameOrderNumberValue(ticket?.order?.orderNumber, orderNumber)
    );
  });
}

export function filterServicesByOrderId(
  services: any[],
  orderId: string | null | undefined
): any[] {
  if (!orderId) {
    return [];
  }

  const target = String(orderId);
  return services.filter((item) => {
    if (item?.order?.orderId != null && String(item.order.orderId) === target) {
      return true;
    }
    if (!Array.isArray(item?.tickets)) {
      return false;
    }
    return item.tickets.some(
      (ticket: any) =>
        ticket?.order?.orderId != null && String(ticket.order.orderId) === target
    );
  });
}

export function filterServicesBySearch(
  items: any[],
  searchActividad: string,
  searchProveedor: string
): any[] {
  let filtered = items ?? [];

  if (searchActividad.trim()) {
    const term = searchActividad.toLowerCase();
    filtered = filtered.filter((item) => {
      const value = (
        item.nombreServicio ||
        item.nameProduct ||
        ''
      ).toLowerCase();
      return value.includes(term);
    });
  }

  if (searchProveedor.trim()) {
    const term = searchProveedor.toLowerCase();
    filtered = filtered.filter((item) => {
      const value = (getProgrammingProvider(item)?.nombre || '').toLowerCase();
      return value.includes(term);
    });
  }

  return filtered;
}

export function buildAutocompleteOptions(
  items: any[],
  searchActividad: string,
  searchProveedor: string
): { actividades: string[]; proveedores: string[] } {
  const filteredForActividad = filterServicesBySearch(
    items,
    '',
    searchProveedor
  );
  const allActividades = Array.from(
    new Set(
      filteredForActividad
        .map((item) => item.nombreServicio || item.nameProduct)
        .filter(Boolean)
    )
  ) as string[];

  const filteredForProveedor = filterServicesBySearch(
    items,
    searchActividad,
    ''
  );
  const allProveedores = Array.from(
    new Set(
      filteredForProveedor
        .map((item) => getProgrammingProvider(item)?.nombre)
        .filter(Boolean)
    )
  ) as string[];

  const actividadTerm = searchActividad.trim().toLowerCase();
  const proveedorTerm = searchProveedor.trim().toLowerCase();

  return {
    actividades: actividadTerm
      ? allActividades.filter((option) =>
          option.toLowerCase().includes(actividadTerm)
        )
      : allActividades,
    proveedores: proveedorTerm
      ? allProveedores.filter((option) =>
          option.toLowerCase().includes(proveedorTerm)
        )
      : allProveedores
  };
}

/** El monto del padre TICKET es la suma de los tickets, no el de uno solo. */
export function syncServiceTotalFromTickets(service: any): void {
  const tickets = Array.isArray(service?.tickets) ? service.tickets : [];
  if (!tickets.length) return;

  let sum = 0;
  let counted = 0;
  for (const ticket of tickets) {
    if (ticket?.isDeleted) continue;
    const value = Number(ticket?.total);
    if (!Number.isFinite(value)) continue;
    sum += value;
    counted += 1;
  }
  if (!counted) return;

  const total = Math.round((sum + Number.EPSILON) * 100) / 100;
  service.total = total;
  service.monto = total;
  const raw = String(service?.moneda ?? service?.tipoMoneda ?? '').toUpperCase();
  const dollar =
    raw === 'D' || raw === 'USD' || raw === '$' || raw === 'DOLAR';
  if (service.editOperation === true) {
    if (dollar) service.newTotalCostoDolares = total;
    else service.newTotalCostoSoles = total;
  }
}
